import type { PermissionCategory } from './types';

interface PermissionLike {
  id: number;
  name: string;
  description: string;
}

export interface CategoryDependencyConfig {
  categoryName: string;
  basePermissionKeys: string[];
  basePermissionDescriptions: string[];
  dependentPermissionKeys: string[];
  dependentPermissionDescriptions: string[];
}

const matchesPermission = (
  permission: PermissionLike,
  names: string[],
  descriptions: string[]
): boolean => {
  return names.includes(permission.name) || descriptions.includes(permission.description);
};

const orderPermissionIdsByCategory = (
  permissionIds: Iterable<number>,
  permissions: PermissionLike[]
): number[] => {
  const permissionIdSet = new Set(permissionIds);

  return permissions
    .map((permission) => permission.id)
    .filter((permissionId) => permissionIdSet.has(permissionId));
};

/**
 * Cria um resolvedor de dependências entre permissões de uma categoria.
 *
 * Marcar qualquer permissão dependente marca automaticamente a permissão base.
 * De forma simétrica, desmarcar a base remove todas as dependentes.
 *
 * Retorna `null` quando a categoria não corresponde ou quando a base/dependentes
 * não existem, permitindo que o chamador mantenha o comportamento padrão de
 * alternância.
 */
export function createCategoryDependencies(config: CategoryDependencyConfig) {
  return function applyCategoryDependencies(
    category: PermissionCategory,
    currentPermissions: number[],
    permissionId: number
  ): number[] | null {
    if (category.name !== config.categoryName) {
      return null;
    }

    const basePermission = category.permissions.find((permission) =>
      matchesPermission(permission, config.basePermissionKeys, config.basePermissionDescriptions)
    );
    const dependentPermissionIds = new Set(
      category.permissions
        .filter((permission) =>
          matchesPermission(
            permission,
            config.dependentPermissionKeys,
            config.dependentPermissionDescriptions
          )
        )
        .map((permission) => permission.id)
    );

    if (!basePermission || dependentPermissionIds.size === 0) {
      return null;
    }

    const nextPermissions = new Set(currentPermissions);
    const isCheckingPermission = !nextPermissions.has(permissionId);

    if (isCheckingPermission) {
      nextPermissions.add(permissionId);

      if (dependentPermissionIds.has(permissionId)) {
        nextPermissions.add(basePermission.id);
      }
    } else {
      nextPermissions.delete(permissionId);

      if (permissionId === basePermission.id) {
        dependentPermissionIds.forEach((id) => nextPermissions.delete(id));
      }
    }

    return orderPermissionIdsByCategory(nextPermissions, category.permissions);
  };
}
